import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchSuppliers } from "../../../store/Slices/SupplierSlice";
import { fetchSupplierLedger } from "../../../store/Slices/SupplierLedgerSlice";
import { fetchCompanyItemSummary } from "../../../store/Slices/CompanyItemSummarySlice";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import { CompanyCompleteLedgerColumns } from "../../../assets/Columns/CompanyCompleteLedgerColumns";
import PayableTxnCardList from "../../../components/Payables/PayableTxnCardList";
import PayableCompanyDetailHero from "../../../components/Payables/PayableCompanyDetailHero";
import { FaArrowAltCircleLeft } from "react-icons/fa";
import { BsSearch } from "react-icons/bs";
import { FiInbox } from "react-icons/fi";

import { getAppBasePath } from "../../../utils/appPaths";
const isSummaryRow = (row) =>
  row?.invoice === "-" && row?.desc === "-" && row?.date === "-";

const TXN_TABS = [
  { id: "all", label: "All In/Out", short: "All" },
  { id: "purchase", label: "Purchases (Bill)", short: "Bills" },
  { id: "payment", label: "Payments (Paid)", short: "Paid" },
];

const PayableDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [txnTab, setTxnTab] = useState("all");

  const SuppliersState = useSelector((state) => state.Suppliers);
  const SupplierLedgers = useSelector((state) => state.SupplierLedgers);
  const CompanyItemSummaryState = useSelector(
    (state) => state.CompanyItemSummaryState
  );

  const company = useMemo(
    () => (SuppliersState.data || []).find((c) => c._id === companyId),
    [SuppliersState.data, companyId]
  );

  useEffect(() => {
    dispatch(fetchSuppliers());
  }, [dispatch]);

  useEffect(() => {
    if (companyId) {
      dispatch(fetchSupplierLedger(companyId));
      dispatch(fetchCompanyItemSummary(companyId));
    }
  }, [companyId, dispatch]);

  const isCartonUnit = (unit) => {
    const u = String(unit || "").trim().toLowerCase();
    return u === "box" || u === "ct" || u === "carton";
  };

  const purchaseItemRows = useMemo(() => {
    const items = CompanyItemSummaryState?.data;
    if (!Array.isArray(items)) return [];
    return items.filter((dt) => dt.name !== "Total");
  }, [CompanyItemSummaryState?.data]);

  const totalBoxQty = useMemo(() => {
    return purchaseItemRows
      .filter((dt) => isCartonUnit(dt.type))
      .reduce((acc, dt) => acc + Number(dt.qty || 0), 0);
  }, [purchaseItemRows]);

  const allRows = useMemo(() => {
    const d = SupplierLedgers?.data;
    return Array.isArray(d?.all) ? d.all : [];
  }, [SupplierLedgers?.data]);

  const ledgerRows = useMemo(() => {
    if (txnTab === "purchase") return allRows.filter((r) => Number(r.dr) > 0);
    if (txnTab === "payment") return allRows.filter((r) => Number(r.cr) > 0);
    return allRows;
  }, [allRows, txnTab]);

  const entryCount = useMemo(
    () => ledgerRows.filter((r) => !isSummaryRow(r)).length,
    [ledgerRows]
  );

  const columns = useMemo(() => {
    if (txnTab === "payment") {
      return CompanyCompleteLedgerColumns.filter(
        (c) => c.id !== "dr" && c.id !== "qty" && c.id !== "price" && c.id !== "name"
      );
    }
    if (txnTab === "purchase") {
      return CompanyCompleteLedgerColumns.filter((c) => c.id !== "cr");
    }
    return CompanyCompleteLedgerColumns;
  }, [txnTab]);

  const base = getAppBasePath();

  if (SuppliersState.loading && !company) {
    return (
      <BodyWrapper>
        <FetchingLoading />
      </BodyWrapper>
    );
  }

  if (!company) {
    return (
      <BodyWrapper>
        <div className="text-center py-12 text-muted">Company not found.</div>
      </BodyWrapper>
    );
  }

  return (
    <BodyWrapper>
      <Header title="Company ledger" desc="Purchase & payment history" Back>
        <button
          type="button"
          className="flex items-center gap-2 text-ink font-semibold text-sm"
          onClick={() => navigate(`${base}/payables`)}
        >
          <FaArrowAltCircleLeft />
          <span className="max480:hidden">Back to list</span>
          <span className="hidden max480:inline">Back</span>
        </button>
      </Header>

      <PayableCompanyDetailHero
        company={company}
        totalBoxQty={totalBoxQty}
        loadingBoxQty={CompanyItemSummaryState.loading}
      />

      {purchaseItemRows.length > 0 && (
        <div className="mb-5 rounded-2xl border border-line bg-card shadow-soft overflow-hidden">
          <div className="px-4 py-3 border-b border-line bg-subtle">
            <h3 className="text-sm font-bold text-ink uppercase tracking-wide">
              Items purchased (qty)
            </h3>
          </div>
          <ul className="divide-y divide-line">
            {purchaseItemRows.map((item) => (
              <li
                key={`${item.name}-${item.type}`}
                className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
              >
                <span className="font-medium text-ink truncate">
                  {item.name}
                  {isCartonUnit(item.type) && (
                    <span className="ml-2 text-[10px] font-bold uppercase text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">
                      Carton
                    </span>
                  )}
                </span>
                <span className="shrink-0 tabular-nums font-semibold text-ink">
                  {Number(item.qty || 0).toLocaleString("en-PK")}{" "}
                  <span className="text-muted font-normal">{item.type}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h3 className="text-sm font-bold text-ink uppercase tracking-wide">
            Transactions
          </h3>
          {!SupplierLedgers.loading && (
            <span className="text-xs text-muted font-medium">
              {entryCount} entries
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {TXN_TABS.map(({ id, label, short }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTxnTab(id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                txnTab === id
                  ? "bg-ink text-white border-ink shadow-soft"
                  : "bg-card text-inkSecondary border-line hover:border-ink/40"
              }`}
            >
              <span className="max720:hidden">{label}</span>
              <span className="hidden max720:inline">{short}</span>
            </button>
          ))}
        </div>

        <div className="relative mb-4">
          <BsSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search transactions..."
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-line bg-card text-sm shadow-soft focus:outline-none focus:border-ink focus:ring-2 focus:ring-ink/15 transition-all"
          />
        </div>
      </div>

      {SupplierLedgers.loading ? (
        <FetchingLoading />
      ) : entryCount === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/80">
          <div className="w-14 h-14 rounded-2xl bg-ink/10 flex items-center justify-center mb-3">
            <FiInbox className="text-ink" size={24} />
          </div>
          <p className="font-semibold text-inkSecondary text-sm">
            No transactions in this tab
          </p>
        </div>
      ) : (
        <div className="pb-4">
          <PayableTxnCardList
            rows={ledgerRows}
            searchText={searchText}
            columns={columns}
          />
        </div>
      )}
    </BodyWrapper>
  );
};

export default PayableDetail;
